import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronDown, FiX } from 'react-icons/fi';

import { TOOL_OPTIONS, type ToolOption } from '../../constants/toolLabels';

interface ToolsTagInputProps {
  /** IDs das tools atualmente selecionadas (valores internos, ex: `send_text_message`) */
  value: string[];
  /** Callback com a nova lista de IDs */
  onChange: (ids: string[]) => void;
  /** Placeholder do input quando vazio */
  placeholder?: string;
  /** IDs que devem ser escondidos do autocomplete (ex: as mesmas da outra lista) */
  excludeIds?: string[];
  /** Base dos testids (input recebe o valor; opções `${base}-option-${id}`) */
  'data-testid'?: string;
}

/**
 * Input estilo "tags" com autocomplete restrito ao catálogo de tools.
 *
 * Diferente do `TagInput` genérico do módulo chat, este NÃO aceita
 * texto livre — o usuário só pode selecionar de uma lista fixa de tools
 * disponíveis (`TOOL_OPTIONS`). Cada chip exibe o label amigável em
 * português (ex: "Enviar mensagem de texto") mas o valor persistido é
 * o ID interno (`send_text_message`).
 *
 * ## Comportamento
 *
 * - Click no input abre o dropdown com todas as tools disponíveis
 * - Digitar filtra por label (ignora case)
 * - Click em uma opção adiciona a tool
 * - X no chip remove a tool
 * - Backspace com input vazio remove a última tool (UX padrão)
 * - Dropdown fecha ao clicar fora ou ao selecionar
 *
 * ## Props
 *
 * - `value`: array de IDs internos (ex: `['send_text_message', 'schedule_meeting']`)
 * - `onChange`: callback chamado com a nova lista
 * - `excludeIds`: IDs a esconder do dropdown (ex: ferramentas já proibidas
 *   não podem também ser esperadas — previne contradições)
 */
export const ToolsTagInput: React.FC<ToolsTagInputProps> = ({
  value,
  onChange,
  placeholder = 'Clique para selecionar ferramentas...',
  excludeIds = [],
  'data-testid': dataTestId = 'tools-tag-input',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Opções disponíveis = catálogo - já selecionadas - explicitamente excluídas
  const availableOptions = useMemo(() => {
    const excluded = new Set([...value, ...excludeIds]);
    const filtered = TOOL_OPTIONS.filter((opt) => !excluded.has(opt.value));

    if (!search.trim()) return filtered;
    const lower = search.toLowerCase();
    return filtered.filter(
      (opt) =>
        opt.label.toLowerCase().includes(lower) ||
        opt.value.toLowerCase().includes(lower) ||
        opt.description?.toLowerCase().includes(lower),
    );
  }, [value, search, excludeIds]);

  // Agrupa por `group` pra exibir categorizado no dropdown
  const groupedOptions = useMemo(() => {
    const groups: Record<string, ToolOption[]> = {};
    availableOptions.forEach((opt) => {
      if (!groups[opt.group]) groups[opt.group] = [];
      groups[opt.group]!.push(opt);
    });
    return groups;
  }, [availableOptions]);

  // Selected chips — lookup pelo catálogo pra pegar o label, com fallback
  const selectedChips = useMemo(() => {
    return value.map((id) => {
      const match = TOOL_OPTIONS.find((opt) => opt.value === id);
      return { id, label: match?.label ?? id };
    });
  }, [value]);

  const handleAddTool = (id: string): void => {
    onChange([...value, id]);
    setSearch('');
    // Mantém o dropdown aberto pra facilitar múltiplas seleções
    inputRef.current?.focus();
  };

  const handleRemoveTool = (id: string): void => {
    onChange(value.filter((v) => v !== id));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && !search && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearch('');
    } else if (e.key === 'Enter' && availableOptions.length === 1 && availableOptions[0]) {
      // Enter seleciona a única opção visível (atalho de teclado comum)
      e.preventDefault();
      handleAddTool(availableOptions[0].value);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Container visual estilo input */}
      <div
        className="w-full min-h-[42px] px-3 py-2 rounded-lg border cursor-text transition-colors"
        style={{
          backgroundColor: '#ffffff',
          borderColor: isOpen ? '#8b5cf6' : 'rgba(0, 0, 0, 0.10)',
        }}
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Chips das tools já selecionadas */}
          {selectedChips.map(({ id, label }) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium"
              style={{
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                color: '#6d28d9',
              }}
            >
              {label}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveTool(id);
                }}
                className="rounded-full p-0.5 transition-colors"
                style={{ color: '#6d28d9' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                aria-label={`Remover ${label}`}
              >
                <FiX className="h-3 w-3" />
              </button>
            </span>
          ))}

          {/* Input de busca */}
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={value.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[140px] outline-none bg-transparent text-sm"
            style={{ color: '#1e293b' }}
            autoComplete="off"
            spellCheck="false"
            data-testid={dataTestId}
          />

          {/* Chevron indicador */}
          <FiChevronDown
            className={`h-4 w-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            style={{ color: '#64748b' }}
          />
        </div>
      </div>

      {/* Dropdown de opções */}
      {isOpen && (
        <div
          className="absolute z-20 left-0 right-0 mt-1 rounded-lg border shadow-lg max-h-72 overflow-y-auto"
          style={{
            backgroundColor: '#ffffff',
            borderColor: 'rgba(0, 0, 0, 0.08)',
          }}
        >
          {availableOptions.length === 0 ? (
            <div className="px-3 py-4 text-xs text-center text-slate-500">
              {search
                ? 'Nenhuma ferramenta encontrada com esse termo.'
                : value.length >= TOOL_OPTIONS.length - excludeIds.length
                  ? 'Todas as ferramentas disponíveis já foram selecionadas.'
                  : 'Nenhuma ferramenta disponível.'}
            </div>
          ) : (
            Object.entries(groupedOptions).map(([groupName, options]) => (
              <div key={groupName}>
                <div
                  className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide sticky top-0"
                  style={{
                    color: '#64748b',
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid rgba(0,0,0,0.04)',
                  }}
                >
                  {groupName}
                </div>
                {options.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleAddTool(opt.value)}
                    className="w-full text-left px-3 py-2 transition-colors"
                    style={{ backgroundColor: 'transparent' }}
                    data-testid={`${dataTestId}-option-${opt.value}`}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.06)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <div
                      className="text-sm font-medium"
                      style={{ color: '#1e293b' }}
                    >
                      {opt.label}
                    </div>
                    {opt.description && (
                      <div
                        className="text-xs mt-0.5"
                        style={{ color: '#64748b' }}
                      >
                        {opt.description}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
